import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaUpload, FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFolder, FaFileContract, FaTrash } from 'react-icons/fa';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { storage, db } from '../../../config/firebaseConfig';
import styles from './ContractsModal.module.css';

const ContractsModal = ({ onClose, project }) => {
  const [uploadedContracts, setUploadedContracts] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    fetchUploadedContracts();
  }, [project.id]);

  // Handle closing when clicking outside the modal
  const handleOverlayClick = useCallback((e) => {
    if (e.target === modalRef.current && !isUploading) {
      onClose();
    }
  }, [onClose, isUploading]);

  // Add escape key listener to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isUploading) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, isUploading]);
  
  // Get file icon based on file type
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    
    if (extension === 'pdf') return <FaFilePdf />;
    if (['doc', 'docx'].includes(extension)) return <FaFileWord />;
    if (['xls', 'xlsx', 'csv'].includes(extension)) return <FaFileExcel />;
    if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) return <FaFileImage />;
    return <FaFile />;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const fetchUploadedContracts = async () => {
    try {
      // Get the project document to fetch existing contracts
      const projectDoc = await getDoc(doc(db, 'Projects', project.id));
      const projectData = projectDoc.data();
      
      if (projectData.contracts) {
        setUploadedContracts(projectData.contracts);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files.length > 0) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    setIsUploading(true);
    try {
      // Create a reference to the file in Firebase Storage
      const fileRef = ref(storage, `projects/${project.id}/contracts/${file.name}`);
      
      // Upload the file
      await uploadBytes(fileRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(fileRef);
      
      // Update the project document in Firestore
      const projectRef = doc(db, 'Projects', project.id);
      const newContract = {
        name: file.name,
        url: downloadURL,
        uploadedAt: new Date().toISOString()
      };
      
      // Check if contracts array exists in the project
      const projectDoc = await getDoc(projectRef);
      const projectData = projectDoc.data();
      
      if (projectData.contracts) {
        await updateDoc(projectRef, {
          contracts: [...projectData.contracts, newContract]
        });
      } else {
        await updateDoc(projectRef, {
          contracts: [newContract]
        });
      }
      
      // Refresh the contracts list
      await fetchUploadedContracts();
      
    } catch (error) {
      console.error('Error uploading contract:', error);
      alert('Sözleşme yükleme hatası');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteContract = async (contractIndex) => {
    const contractToDelete = uploadedContracts[contractIndex];
    
    if (window.confirm(`"${contractToDelete.name}" sözleşmesini silmek istediğinizden emin misiniz?`)) {
      try {
        // Remove contract from the array
        const updatedContracts = uploadedContracts.filter((_, index) => index !== contractIndex);
        
        // Update the project document in Firestore
        const projectRef = doc(db, 'Projects', project.id);
        await updateDoc(projectRef, {
          contracts: updatedContracts
        });
        
        // Refresh the contracts list
        setUploadedContracts(updatedContracts);
        
        alert('Sözleşme başarıyla silindi');
      } catch (error) {
        console.error('Error deleting contract:', error);
        alert('Sözleşme silinirken hata oluştu');
      }
    }
  };

  return (
    <div 
      className={styles.modalOverlay}
      ref={modalRef}
      onClick={handleOverlayClick}
    >
      <div className={styles.modalContent}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaFileContract style={{ marginRight: '0.5rem' }} />
            Sözleşme Yönetimi
          </h2>
          <button 
            onClick={onClose}
            className={styles.closeButton}
            disabled={isUploading}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* File Upload Area */}
          <div 
            className={styles.dropzone}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <div className={styles.dropzoneIcon}>
              <FaUpload />
            </div>
            <p className={styles.dropzoneText}>Sözleşme Dosyalarınızı Sürükleyin</p>
            <p className={styles.dropzoneText}>veya</p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className={styles.fileInput}
              disabled={isUploading}
              accept=".pdf,.doc,.docx,.xls,.xlsx"
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current.click();
              }}
              disabled={isUploading}
              className={styles.uploadButton}
              type="button"
            >
              {isUploading ? (
                <>
                  <span className={styles.loadingSpinner}></span>
                  Yükleniyor...
                </>
              ) : (
                <>
                  <FaUpload />
                  Sözleşme Seçin
                </>
              )}
            </button>
          </div>

          {/* Uploaded Contracts List */}
          <div className={styles.fileListSection}>
            <h3 className={styles.fileListTitle}>
              <FaFolder style={{ marginRight: '0.5rem' }} />
              Yüklenmiş Sözleşmeler
            </h3>
            <div className={styles.fileList}>
              {uploadedContracts.length > 0 ? (
                uploadedContracts.map((contract, index) => (
                  <div key={index} className={styles.fileItem}>
                    <div className={styles.contractHeader}>
                      <button
                        className={styles.deleteButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteContract(index);
                        }}
                        title="Sözleşmeyi Sil"
                      >
                        <FaTrash />
                      </button>
                    </div>
                    <div 
                      className={styles.fileLink}
                      onClick={() => {
                        // Firebase Storage URL'ini görüntüleme için dönüştür
                        let viewUrl = contract.url;
                        
                        // Firebase Storage URL'ini embed edilebilir formata çevir
                        if (contract.url.includes('firebasestorage.googleapis.com')) {
                          // URL'den token'ı al
                          const urlParts = contract.url.split('?');
                          const baseUrl = urlParts[0];
                          const params = urlParts[1];
                          
                          // Dosya uzantısını kontrol et
                          const fileName = contract.name.toLowerCase();
                          
                          if (fileName.endsWith('.pdf')) {
                            // PDF için Google Docs Viewer kullan
                            viewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(contract.url)}&embedded=true`;
                          } else {
                            // Diğer dosyalar için orijinal URL'i kullan ama Content-Disposition'ı değiştirmeye çalış
                            viewUrl = contract.url.replace('&response-content-disposition=attachment', '');
                            if (!viewUrl.includes('response-content-disposition')) {
                              const separator = viewUrl.includes('?') ? '&' : '?';
                              viewUrl = viewUrl + separator + 'response-content-disposition=inline';
                            }
                          }
                        }
                        
                        // Dosyayı yeni sekmede aç
                        const newWindow = window.open(viewUrl, '_blank');
                        if (newWindow) {
                          newWindow.focus();
                        }
                      }}
                      title="Dosyayı görüntülemek için tıklayın"
                    >
                      {getFileIcon(contract.name)}
                      {contract.name}
                    </div>
                    {contract.uploadedAt && (
                      <small style={{ color: 'var(--file-type)', fontSize: '0.7rem' }}>
                        {formatDate(contract.uploadedAt)}
                      </small>
                    )}
                  </div>
                ))
              ) : (
                <div className={styles.noFilesMessage}>
                  Henüz sözleşme yüklenmemiş
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractsModal;
